/**
 * frontend/components/StageArc.jsx
 * SoundDAO Sprint 5C — Stage progression arc visualization
 *
 * POST-DEPLOY FIX (Aug 14 2026, punch-list items 1 & 2).
 * This component owned a FOURTH hardcoded copy of the stage gates — Raw 5,
 * Flair 25, Burning 50, Wildfire 100 — which the Sprint 6-Zero audit missed.
 * It is rendered on BOTH the creator dashboard and the PUBLIC creator profile,
 * so it is the copy users actually look at, and after Season Zero it was the
 * only one still quoting pre-Season-Zero numbers: JJ Boogie showed "3 fans to
 * Flair" while already meeting the 2-fan gate.
 *
 * It also advertised "100 fans to Phoenix". Phoenix requires FReN, which is not
 * live — a Milestone 1 truthfulness violation on a public surface.
 *
 * Gates now arrive as a prop from the backend (GET /api/creators/:id →
 * stage_gates, computed by backend/lib/stageGates.js). The FALLBACK below is
 * the Season Zero values, used only if a caller forgets to pass them.
 *
 * Props:
 *   stage          — current creator stage string
 *   totalFans      — total fan count (int)
 *   heatScore      — heat score 0-100 (int)
 *   contentStreak  — content streak day count (int)
 *   gates          — served stage_gates: { Flair:{total_fans}, Burning:{...}, Wildfire:{...} }
 *   progressLabel  — optional served sentence; overrides the computed label
 *   mini           — if true, render compact inline version
 */

import React from 'react';

// Wildfire is the Season Zero ceiling. Phoenix stays in the arc as a visible
// end-point but is never described as reachable.
const STAGE_ORDER = ['Raw', 'Flair', 'Burning', 'Wildfire', 'Phoenix'];

// Which gate a creator at each stage is working TOWARD.
const NEXT_GATE_KEY = { Raw: 'Flair', Flair: 'Burning', Burning: 'Wildfire' };

// Season Zero fallback — mirrors backend/lib/stageGates.js. Used only when the
// caller does not pass served gates.
const FALLBACK_GATES = {
  Flair:    { total_fans: 2  },
  Burning:  { total_fans: 10 },
  Wildfire: { total_fans: 25 },
};

function fansNeeded(stage, gates) {
  const key = NEXT_GATE_KEY[stage];
  if (!key) return null;                       // Wildfire / Phoenix: no next gate
  const g = (gates && gates[key]) || FALLBACK_GATES[key];
  return g ? Number(g.total_fans) : null;
}

function getProgress(stage, totalFans, gates) {
  if (stage === 'Wildfire' || stage === 'Phoenix') return 100;
  const need = fansNeeded(stage, gates);
  if (!need) return 0;
  return Math.min((Number(totalFans) || 0) / need, 1) * 100;
}

function getProgressLabel(stage, totalFans, gates) {
  if (stage === 'Phoenix') return 'You made it';
  // Wildfire is the top of the current arc — do NOT dangle Phoenix, which
  // requires FReN and is not live.
  if (stage === 'Wildfire') return 'Top of the current arc';

  const need = fansNeeded(stage, gates);
  const key  = NEXT_GATE_KEY[stage];
  if (!need || !key) return '';

  const remaining = Math.max(0, need - (Number(totalFans) || 0));
  // Gate already met: the nightly cron advances them, so say that rather than
  // showing a stale "N fans to go" that reads as unfinished work.
  if (remaining === 0) return `${key} unlocked — advancing tonight`;
  return `${remaining} fan${remaining === 1 ? '' : 's'} to ${key}`;
}

export default function StageArc({ stage = 'Raw', totalFans = 0, gates = null, progressLabel = null, mini = false }) {
  const currentIndex = STAGE_ORDER.indexOf(stage);
  const progress     = getProgress(stage, totalFans, gates);
  const label        = progressLabel || getProgressLabel(stage, totalFans, gates);

  const dotSize    = mini ? 10 : 18;
  const fontSize   = mini ? '9px' : '10px';
  const gap        = mini ? '0px' : '4px';

  return (
    <div style={{ padding: mini ? '4px 0' : '12px 0' }}>
      {/* Node row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
      }}>
        {STAGE_ORDER.map((s, i) => {
          const isDone   = i < currentIndex;
          const isActive = i === currentIndex;

          // Connecting line (before each node except the first)
          const line = i > 0 ? (
            <div style={{
              flex: 1,
              height: '2px',
              background: i <= currentIndex ? '#7c3aed' : 'rgba(255,255,255,0.15)',
              margin: `0 ${mini ? '2px' : '4px'}`,
            }} />
          ) : null;

          return (
            <React.Fragment key={s}>
              {line}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap,
                minWidth: mini ? '28px' : '40px',
              }}>
                {/* Dot */}
                <div style={{
                  width: dotSize,
                  height: dotSize,
                  borderRadius: '50%',
                  background: isDone ? '#7c3aed'
                    : isActive ? '#f97316'
                    : 'transparent',
                  border: isDone ? '2px solid #7c3aed'
                    : isActive ? '2px solid #f97316'
                    : '2px solid rgba(255,255,255,0.2)',
                  boxShadow: isActive ? '0 0 0 4px rgba(249,115,22,0.2)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {isDone && (
                    <div style={{
                      width: mini ? 3 : 5,
                      height: mini ? 3 : 5,
                      borderRadius: '50%',
                      background: 'white',
                    }} />
                  )}
                </div>
                {/* Label */}
                <span style={{
                  fontSize,
                  color: isActive ? '#f97316' : 'rgba(255,255,255,0.4)',
                  fontWeight: isActive ? '600' : '400',
                  whiteSpace: 'nowrap',
                }}>
                  {s}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress bar — full mode only */}
      {!mini && (
        <div style={{ marginTop: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <div style={{
              flex: 1,
              height: '4px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '2px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: '#f97316',
                borderRadius: '2px',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.5)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              {label}
            </span>
          </div>
        </div>
      )}

      {/* Mini progress label */}
      {mini && (
        <div style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.45)',
          marginTop: '4px',
          textAlign: 'center',
        }}>
          {stage !== 'Raw' && <span style={{ color: '#f97316' }}>{stage}</span>}
          {stage !== 'Raw' && ' · '}
          {label}
        </div>
      )}
    </div>
  );
}
